<script setup>
import { computed, ref, nextTick, onMounted } from 'vue';
import { useQuery } from 'villus';
import find from 'lodash-es/find';
import { useRoute, useRouter } from 'vue-router';
import CustomSelect from '@/components/custom-select/CustomSelect.vue';
import Card from '@/components/card/Card.vue';
import Loader from '@/components/loader/Loader.vue';
import { GA } from '@/mixins/Ga';
import { useStore } from '@/store/Store.js';
import { useLenis } from '@/components/scroll/Lenis.js';
import WaveTop from '@/icons/wave-top.svg';
import WaveCurve from '@/icons/wave-curve.svg';
import Filter from '@/icons/filter.svg';
import StickyBar from '@/components/sticky-bar/StickyBar.vue';
import BlogFeaturedList from '@/components/blog-cta/BlogFeaturedList.vue';
import Header from '@/components/header/Header.vue';

import { imageTransform } from '@/components/flexible/FlexibleQuery.js';

const store = useStore();
const route = useRoute();
const router = useRouter();
const limit = ref(12);
const offset = ref(0);
const search = ref('');
const currentCategory = ref(null);
const blogs = ref([]);
const hasMore = ref(true);
let loadingPagination = false;
const routeId = route.params.id;
const ga = new GA({ category: 'midBody', action: 'clicInterne' });
const listkey = ref('blogstart');
const themeColor = ref('yellow');
const extraCrumbs = ref([]);
const lenis = useLenis();
const previousScroll = ref(0);
const mounted = ref(false);
const thedata = ref(null);
const rootEl = ref(null);

const defaultCategorieType = {
    id: null,
    slug: 'all',
    title: 'Catégorie',
};
const defaultTagType = {
    id: null,
    slug: 'all',
    title: 'Tag',
};

const setCategorie = (e) => {
    if (e.id) {
        router.push({
            id: 'blogs',
            params: {
                category: e.slug,
                slug: e.slug,
                id: e.id,
            },
        });
    } else {
        router.push({
            name: `blogs.${store.lang.value}`,
        });
    }
};

const more = () => {
    loadingPagination = true;
    offset.value += limit.value;
    previousScroll.value = store.windowScroll.value.y;
};

const variables = computed(() => ({
    lang: store.lang.value,
    limit: limit.value,
    offset: offset.value,
    id: routeId || null,
    order: search.value === '' ? 'postDate DESC' : 'score DESC',
}));

const blogsQuery = `
    query FetchEntries($lang: [String], $limit: Int, $offset: Int, $id: [QueryArgument], $order: String) {
        blogs : entry (section: "blogs", site: $lang) {
            title,
            ... on blogs_Entry {
                blogsBlogCta
                color
                subtitle
                suptitle
                supsubtitle
                personnageEntete {
                    inline
                }
                sectionHandle
                featuredBlogsTitle
                featuredBlogs {
                    id,
                    title,
                    uri,
                    day: postDate @formatDateTime (format: "d"),
                    month: postDate @formatDateTime (format: "n"),
                    year: postDate @formatDateTime (format: "Y"),
                    ... on blog_Entry {
                        category: blogCategory { ... on blogcategories_default_Entry { title } },
                        cover: blogCover ${imageTransform(800, 426)}
                    }
                }
            }
        },
        entries (section: "blog", site: $lang, orderBy: $order, limit: $limit, offset: $offset, relatedTo: $id ) {
            id,
            title,
            uri,
            day: postDate @formatDateTime (format: "d"),
            month: postDate @formatDateTime (format: "n"),
            year: postDate @formatDateTime (format: "Y"),
            ... on blog_Entry {
                category: blogCategory { ... on blogcategories_default_Entry { title } },
                cover: blogCover ${imageTransform(800, 426)}
            }
        },
        total: entryCount(section: "blog", site: $lang, orderBy: $order, limit: $limit, offset: $offset, relatedTo: $id ),
        categories: entries (site: $lang, section: "blogCategories") {
            id,
            slug,
            title
            url
            ... on blogcategories_default_Entry { titlePage: blogCategoryTitle }
        },
        tags: entries (site: $lang, section: "tags") {
            id,
            slug,
            title
            url
        }
    }`;

onMounted(() => {
    themeColor.value = thedata?.value?.blogs?.color ?? 'blue';

    store.setHeaderColor({
        mainColor: themeColor.value,
        wave: `light-${themeColor.value}`,
        theme: 'light',
        normal: { bg: 'white', txt: `light-${themeColor.value}` },
        over: { bg: 'white', txt: `light-${themeColor.value}` },
        prenav: {
            bg: `bg-dark-${themeColor.value}`,
            txt: 'text-white',
        },
        nav: {
            txt: 'text-white',
            hover: `bg-dark-${themeColor.value}`,
            burger: 'text-white',
            lines: `text-light-${themeColor.value}`,
        },
    });

    mounted.value = true;
});

const {
    data: mainData,
    isDone,
    isFetching,
    onData,
} = await useQuery({
    variables,
    query: blogsQuery,
});

onData((data) => {
    if (!isDone.value) return;

    let preserveScroll = false;
    if (previousScroll.value === store.windowScroll.value.y) {
        preserveScroll = true;
    }

    // reset the data completely only if we're not loading pagination
    if (!loadingPagination) {
        blogs.value = [];
    }
    blogs.value.push(...data.entries);
    hasMore.value = blogs.value.length !== data.total;
    currentCategory.value = find(data.categories, (category) => category.slug === route.params.slug);
    listkey.value = blogs.value.map((r) => r.id).join('');

    if (currentCategory.value) {
        extraCrumbs.value = [
            { url: '/blogs', text: 'Blogue' },
            { url: currentCategory.value.url, text: currentCategory.value.title },
        ];
    }
    // if (!currentCategory.value) {
    //     currentCategory.value = data.categories[0];
    // }

    nextTick(() => {
        if (preserveScroll) {
            lenis.value.scrollTo(previousScroll.value, { immediate: true });
        }
    });

    loadingPagination = false;
    isDone.value = false;
});
</script>

<template>
    <div
        ref="rootEl"
        class="Blogs"
        :class="['bg-bright-' + themeColor, pageClasses]"
    >
        <template v-if="mounted && mainData">
            <div class="Blogs__content">
                <Header
                    :data="mainData.blogs"
                    :title="mainData.blogs.title"
                    :color="'light-' + themeColor"
                    :title-color="'dark-' + themeColor"
                    type-pancarte="sentier"
                    class="mb-6"
                />

                <StickyBar
                    :colors="{
                        txt: 'light-' + themeColor,
                        bg: 'dark-' + themeColor,
                    }"
                >
                    <div class="flex w-[48%] items-center font-bold md:mr-4 md:w-auto">
                        <Filter class="h-4 w-auto fill-current" />
                        <div class="ml-2 md:ml-5">Filtre</div>
                    </div>

                    <CustomSelect
                        :options="[defaultCategorieType, ...mainData.categories]"
                        :preselected-id="routeId"
                        class="select select-categorie-type relative z-9 mr-2.5 w-[48%] md:w-auto"
                        :colors="{ bg: 'light-' + themeColor, text: 'dark-' + themeColor }"
                        side="right"
                        v-on:input="setCategorie($event)"
                    />

                    <!-- <CustomSelect
                        :options="[defaultTagType, ...mainData.tags]"
                        :preselected-id="routeId"
                        class="select select-tag-type relative z-10 w-[48%] md:ml-auto md:w-auto"
                        :colors="{bg:'light-'+themeColor, text:'dark-'+themeColor}"
                        :side="portrait ? 'right': 'left'"
                        v-on:input="setCategorie($event)"
                    /> -->
                </StickyBar>

                <BlogFeaturedList
                    v-if="!currentCategory"
                    class="mb-12"
                    :title-top="mainData.blogs.featuredBlogsTitle"
                    :color="'dark-' + themeColor"
                    :blogs="mainData.blogs.featuredBlogs"
                />

                <div
                    :key="listkey"
                    class="Blogs__blocks relative"
                >
                    <h3
                        v-if="!currentCategory"
                        class="h4 relative z-10 mb-4 px-6 pt-12 text-white md:mb-10 md:pt-0 xl:px-20"
                        :class="['md:text-dark-' + themeColor]"
                    >
                        Toutes nos actualités
                    </h3>
                    <div
                        class="relative z-10 grid grid-cols-1 gap-8 px-6 md:grid-cols-2 lg:grid-cols-3 xl:gap-16 xl:px-20"
                        :class="['text-dark-' + themeColor]"
                    >
                        <template
                            v-for="blog in blogs"
                            :key="'blog' + blog.uri"
                        >
                            <Card
                                :post="blog"
                                class="text-white"
                            />
                        </template>
                        <div
                            v-if="blogs.length == 0"
                            class="my-12 w-full px-6 text-center text-xl md:col-span-2"
                            :class="['text-dark-' + themeColor]"
                        >
                            Aucun article ne correspond à vos critères de recherche.
                        </div>
                    </div>

                    <div
                        aria-hidden
                        class="FeaturedGrid__background absolute bottom-0 left-0 z-0 h-full w-full md:h-[calc(100%-10rem)] xl:block"
                        :class="['bg-light-' + themeColor]"
                    >
                        <WaveCurve
                            :class="['text-light-' + themeColor]"
                            class="absolute top-[2px] h-auto w-full -translate-y-full fill-current md:hidden"
                        />
                        <WaveTop
                            :class="['text-light-' + themeColor]"
                            class="absolute top-0 hidden h-auto w-full translate-y-[-15%] rotate-180 -scale-x-100 fill-current md:block"
                        />
                    </div>
                </div>

                <div
                    class="flex justify-center py-8 xl:py-12"
                    :class="['bg-light-' + themeColor]"
                >
                    <button
                        v-if="hasMore"
                        class="BtnPaginationgroup mx-auto inline-flex cursor-pointer items-center rounded-xl px-4 py-2 text-sm font-medium text-white"
                        :class="[
                            'bg-dark-' + themeColor,
                            {
                                'opacity-50': isFetching,
                            },
                        ]"
                        :disabled="isFetching"
                        v-on:click.stop="more"
                    >
                        <span>Voir plus d'articles</span>
                        <Loader
                            v-if="isFetching"
                            size="small"
                            class="ml-2"
                            bg="transparent"
                            line="white"
                        />
                    </button>
                </div>
            </div>
        </template>
    </div>
</template>
